import { Org, OrgFeatures } from "@/types/org";
import { apiFetch } from "@/lib/api";
import { GAIL_API } from "@/utils/common/constants/routes";

/**
 * Merges dedicated feature-endpoint data into an org object.
 * Used by fetchResources to overlay GetOrgFeatures() onto GetOrg().
 */
export function mergeOrgFeatures(org: Org, features: OrgFeatures | null): Org {
  if (!features) return org;
  return { ...org, features: { ...org.features, ...features } };
}

/**
 * After organization PATCH, the org DTO can omit overlay-only fields
 * (notably scriptsApiVersion). Keep previous overlay values whenever the
 * PATCH body does not include them so a settings save cannot fall back to v1.
 */
export function preserveOrgOverlayFeatures(previousOrg: Org, updatedOrg: Org): Org {
  return {
    ...updatedOrg,
    features: {
      ...previousOrg.features,
      ...updatedOrg.features,
    },
  };
}

export type SaveOrgFeaturesCallbacks = {
  onSavingStart: () => void;
  onSavingEnd: () => void;
  onSuccess: (updatedOrg: Org) => void;
  onError: () => void;
};

/**
 * Patches organization features via the dedicated API endpoint.
 * Returns the updated org or null on failure.
 */
export async function patchOrgFeatures(
  currentOrg: Org,
  features: Partial<OrgFeatures>,
  callbacks: SaveOrgFeaturesCallbacks,
  options?: { silent?: boolean }
): Promise<Org | null> {
  const silent = options?.silent ?? false;

  if (!silent) {
    callbacks.onSavingStart();
  }

  const updatedFeatures = await apiFetch<OrgFeatures>(
    `${GAIL_API}/.internal/customer/organization/features`,
    "PATCH",
    {
    body: features,
  },
  );

  if (!silent) {
    callbacks.onSavingEnd();
  }

  if (updatedFeatures) {
    const updatedOrg = mergeOrgFeatures(currentOrg, updatedFeatures);
    window.localStorage.setItem("orgData", JSON.stringify(updatedOrg));
    callbacks.onSuccess(updatedOrg);

    return updatedOrg;
  }

  if (!silent) {
    callbacks.onError();
  }

  return null;
}
